<template>
  <div :style="{ marginTop: '20px', paddingBottom: '80px' }">

    <div v-if="!loading" style="text-align: left; color: #666;" class="d-flex align-items-center justify-content-center">
      <form @submit.prevent="save_config">
        <table class="fa-settings-table" width="90%" style="font-size: 18px; font-weight: bold" cellpadding="10px">
          <!-- Feature Toggle — sys-admin only. prior code forgot the v-if
               gate and the class alone wasn't hiding anything, so customer-
               admins were seeing this row. -->
          <tr v-if="isAdmin" class="admin-only-row" data-field="fleet_assistant_enabled">
            <td align="right" width="400">Enabled</td>
            <td width="500"><b-form-select v-model="model.fleet_assistant_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Enable Fleet Assistant for this tenant's admin users
            </td>
          </tr>

          <!-- Usage -->
          <tr>
            <td colspan="3" style="padding-top: 10px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Usage</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr>
            <td align="right" width="400" style="vertical-align: top;">Monthly Usage</td>
            <td width="500">
              <div class="usage-quota-wrap">
                <b-progress :max="100" height="14px" class="usage-progress">
                  <b-progress-bar
                    :value="usagePercent"
                    :variant="usageBarVariant"
                  ></b-progress-bar>
                  <b-progress-bar :value="usageFreePercent" variant="light" class="usage-remaining-bar"></b-progress-bar>
                </b-progress>
                <div class="usage-meta">
                  <span class="usage-quota-remaining">{{ usageRemainingDisplay }}% remaining</span>
                  <span class="usage-period usage-period-right">{{ usagePeriodLabel }}</span>
                </div>
                <div v-if="isAdmin && currentMonthUsage" class="usage-current-summary">
                  <span class="usage-current-stat">{{ currentMonthUsage.blended_cost_dollars }}</span>
                  <span class="usage-current-sep">&middot;</span>
                  <span class="usage-current-stat">{{ currentMonthUsage.chat_requests }} {{ currentMonthUsage.chat_requests === 1 ? 'chat' : 'chats' }}</span>
                  <span class="usage-current-sep">&middot;</span>
                  <span class="usage-current-stat">{{ currentMonthUsage.doc_categorizations }} {{ currentMonthUsage.doc_categorizations === 1 ? 'doc upload' : 'doc uploads' }}</span>
                  <span class="usage-current-sep">&middot;</span>
                  <span class="usage-current-stat">{{ formatTokenCount(currentMonthUsage.input_tokens + currentMonthUsage.output_tokens) }} tokens</span>
                  <a href="#" class="usage-details-link" @click.prevent="openUsageDetails">
                    View Details
                  </a>
                </div>
                <a
                  v-else-if="isAdmin"
                  href="#"
                  class="usage-details-toggle"
                  @click.prevent="openUsageDetails"
                >
                  Usage Details
                </a>
              </div>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Need more?
                  <a href="#" @click.prevent="$root.$emit('open-ai-usage-request-modal')">Open a support ticket</a>
                  to request additional capacity.
            </td>
          </tr>
          <tr v-if="canEditCostLimit" class="admin-only-row">
            <td align="right" width="400">Monthly Cost Limit</td>
            <td width="500">
              <b-input-group size="sm">
                <b-input-group-prepend is-text>$</b-input-group-prepend>
                <b-form-input
                  v-model="usageLimitDollars"
                  type="number"
                  min="0"
                  step="1"
                  autocomplete="off"
                ></b-form-input>
              </b-input-group>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Tenant-level monthly budget cap for AI usage. Set to $0 to disable AI for this tenant.
            </td>
          </tr>
          <tr v-if="isAdmin" class="admin-only-row">
            <td align="right" width="400">Reset Usage Stats</td>
            <td width="500">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="reset_usage_stats"
                :disabled="resettingUsage"
              >
                <span v-if="resettingUsage"><b-spinner small></b-spinner> Resetting...</span>
                <span v-else>Reset Current Month Usage</span>
              </b-button>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Clears this month&apos;s Fleet Assistant usage counters for the tenant.
            </td>
          </tr>

          <!-- AI Model Settings — entire section is sys-admin only -->
          <tr v-if="isAdmin" class="admin-only-row">
            <td colspan="3" style="padding-top: 20px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">AI Model</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr v-if="isAdmin" class="admin-only-row" data-field="openai_model">
            <td align="right" width="400">Fleet Assistant model</td>
            <td width="500">
              <b-form-select v-model="model.openai_model" :options="openai_model_options"></b-form-select>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Higher models are smarter but cost more. GPT-5.4 is the most capable.</td>
          </tr>
          <tr v-if="isAdmin" class="admin-only-row" data-field="openai_doc_model">
            <td align="right" width="400">Document model</td>
            <td width="500">
              <b-form-select v-model="model.openai_doc_model" :options="openai_doc_model_options"></b-form-select>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Model used to categorize uploaded docs (type, audience, topics). Default GPT-4.1 nano is cheapest.</td>
          </tr>
          <tr v-if="isAdmin" class="admin-only-row" data-field="openai_api_key">
            <td align="right" width="400">OpenAI API Key</td>
            <td width="500">
              <b-form-input v-model="model.openai_api_key" type="password" placeholder="Leave blank to use default"></b-form-input>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Tenant-specific override for billing</td>
          </tr>

          <!-- Appearance Settings -->
          <tr>
            <td colspan="3" style="padding-top: 30px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Appearance</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr data-field="fleet_assistant_animation">
            <td align="right" width="400">Thinking Animation</td>
            <td width="500">
              <b-form-select v-model="model.fleet_assistant_animation" :options="animation_options"></b-form-select>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Animation shown while AI is thinking</td>
          </tr>
          <tr>
            <td align="right" width="400">Preview</td>
            <td width="500">
              <div class="animation-preview">
                <div class="thinking-indicator">
                  <component :is="currentAnimationComponent" />
                </div>
              </div>
            </td>
            <td width="350">&nbsp;</td>
          </tr>

          <!-- Behavior Settings -->
          <tr>
            <td colspan="3" style="padding-top: 30px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Behavior</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <!-- custom instructions is stacked (label, textarea, help) full
               width instead of the usual 3-column layout because the
               placeholder carries multi-line examples that need room
               to breathe. stacking reads identically on desktop and
               mobile. -->
          <tr data-field="fleet_assistant_instructions">
            <td colspan="3" class="fa-stacked-cell">
              <div class="fa-stacked-label">Custom Instructions</div>
              <b-form-textarea
                v-model="model.fleet_assistant_instructions"
                placeholder="Add tenant-specific instructions for the AI assistant...

Examples:
- Always mention our company name 'ABC Transport' in responses
- Drivers should be referred to as 'operators'
- When discussing bookings, always include the customer name
- Prioritize fuel efficiency in routing suggestions"
                class="fa-stacked-textarea"
              ></b-form-textarea>
              <div class="fa-stacked-charcount">
                {{ (model.fleet_assistant_instructions || '').length }} / 2000 characters
              </div>
              <div class="fa-stacked-help">
                Additional instructions for the Fleet Assistant to follow. Use this to customize terminology, add company-specific rules, or guide how the assistant responds.
              </div>
            </td>
          </tr>

          <!-- Company documents (admin) -->
          <tr>
            <td colspan="3" style="padding-top: 30px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Documents</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr data-field="company_documents_in_help_center">
            <td align="right" width="400">Show in Help Center</td>
            <td width="500"><b-form-select v-model="model.company_documents_in_help_center" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Show company documents in the Help Center's Documents tab for all users
            </td>
          </tr>
          <tr data-field="company_documents_in_fleet_assistant">
            <td align="right" width="400">Use in Fleet Assistant</td>
            <td width="500"><b-form-select v-model="model.company_documents_in_fleet_assistant" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Include company documents as context when Fleet Assistant answers questions
            </td>
          </tr>
          <tr data-field="user_document_uploads_enabled">
            <td align="right" width="400">Allow User Doc Uploads</td>
            <td width="500"><b-form-select v-model="model.user_document_uploads_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Allow users to upload personal documents for Fleet Assistant to reference
            </td>
          </tr>
          <tr>
            <td colspan="3">
              <p style="font-size: 13px; color: #666; margin-bottom: 12px; font-weight: normal;">
                Add company documents for Fleet Assistant to reference, such as a company handbook.  The assistant uses these when answering questions.</br>
                Uploads are limited to PDF, DOCX, or TXT files (max 25 documents, 25 MB each).
              </p>

              <div v-if="docStoresLoading" class="text-muted small">Loading document stores…</div>
              <table v-else class="doc-stores-table">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th style="text-align: right;"># Docs</th>
                    <th style="text-align: right;">Total Size</th>
                    <th style="width: 120px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="store in docStoresForDisplay" :key="store.owner" :class="{ 'store-company': store.owner === 'company' }">
                    <td class="store-owner">
                      <span v-if="store.owner === 'company'" class="user-label company-label">General Documents</span>
                      <span v-else class="user-label">{{ store.owner }}</span>
                    </td>
                    <td class="store-count">
                      <span
                        v-if="store.processing_count"
                        class="doc-badge processing pulsing store-processing"
                      >
                        LEARNING {{ store.processing_count }}
                      </span>
                      {{ store.doc_count }}
                    </td>
                    <td class="store-size">{{ formatFileSize(store.total_size) }}</td>
                    <td class="store-actions">
                      <b-button
                        v-if="store.owner === 'company'"
                        size="sm"
                        variant="outline-primary"
                        @click="openCompanyDocModal"
                      >
                        Manage
                      </b-button>
                      <b-button
                        v-else
                        size="sm"
                        variant="outline-danger"
                        @click="purge_user_docs(store.owner)"
                        :disabled="purgingUser === store.owner"
                      >
                        <span v-if="purgingUser === store.owner"><b-spinner small></b-spinner></span>
                        <span v-else>Purge</span>
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Company Documents Modal -->
              <b-modal
                v-model="showCompanyDocModal"
                title="Manage Company Documents"
                size="lg"
                hide-footer
                @hidden="onCompanyDocModalClosed"
              >
                <p style="font-size: 13px; color: #666; margin-bottom: 12px;">
                  Add company documents for Fleet Assistant to reference, such as the employee handbook. The assistant uses these when answering questions. Upload PDF, DOCX, or TXT files. Max 25 MB per file. Max 25 documents.
                </p>

                <!-- Upload row -->
                <div class="d-flex align-items-center gap-2 mb-3">
                  <input
                    ref="companyDocPicker"
                    type="file"
                    accept=".pdf,.docx,.txt"
                    style="display: none"
                    multiple
                    @change="onCompanyDocPicked"
                  />
                  <b-button size="sm" variant="primary" @click="$refs.companyDocPicker.click()">
                    Add Document(s)
                  </b-button>
                  <b-button
                    v-if="selectedDocs.length > 0"
                    class="ml-2"
                    size="sm"
                    variant="danger"
                    @click="delete_selected_docs"
                    :disabled="deletingSelected"
                  >
                    <span v-if="deletingSelected"><b-spinner small></b-spinner> DELETING {{ deleteProgress }}</span>
                    <span v-else>Delete Selected ({{ selectedDocs.length }})</span>
                  </b-button>
                  <span v-if="companyDocsError" class="text-danger small">{{ companyDocsError }}</span>
                </div>

                <!-- Summary -->
                <div v-if="companyDocs.length > 0" class="docs-summary mb-2">
                  <strong>{{ companyDocs.length }}</strong> document{{ companyDocs.length === 1 ? '' : 's' }} · <strong>{{ formatFileSize(companyDocsTotalSize) }}</strong> total
                </div>

                <!-- Documents table -->
                <div v-if="companyDocsLoading" class="text-muted small">Loading documents…</div>
                <div v-else-if="companyDocs.length === 0" class="text-muted small">No company documents uploaded yet.</div>
                <table v-else class="company-docs-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">
                        <input
                          type="checkbox"
                          :checked="allDocsSelected"
                          :indeterminate.prop="someDocsSelected && !allDocsSelected"
                          @change="toggleSelectAll"
                        />
                      </th>
                      <th>Name</th>
                      <th>Uploaded By</th>
                      <th>Date</th>
                      <th>Size</th>
                      <th style="width: 80px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="doc in companyDocs"
                      :key="doc.doc_id || doc.temp_id"
                      :class="{ 'row-selected': doc.doc_id && selectedDocs.includes(doc.doc_id), 'row-uploading': doc.status === 'uploading' || doc.status === 'processing', 'row-failed': doc.status === 'failed' || doc.status === 'delete-failed', 'row-deleting': doc.status === 'deleting' }"
                    >
                      <td>
                        <input
                          type="checkbox"
                          :checked="doc.doc_id && selectedDocs.includes(doc.doc_id)"
                          :disabled="!doc.doc_id || doc.status === 'uploading' || doc.status === 'processing' || doc.status === 'deleting'"
                          @change="toggleDocSelection(doc.doc_id)"
                        />
                      </td>
                      <td class="doc-name" :title="doc.display_name">{{ doc.display_name }}</td>
                      <td class="doc-uploader">{{ doc.uploaded_by || '—' }}</td>
                      <td class="doc-date">
                        <span v-if="doc.status === 'uploading'" class="doc-badge upload">UPLOADING</span>
                        <span v-else-if="doc.status === 'processing'" class="doc-badge processing pulsing">LEARNING</span>
                        <span v-else-if="doc.status === 'deleting'" class="doc-badge deleting pulsing">DELETING</span>
                        <span v-else-if="doc.status === 'delete-failed'" class="doc-badge failed">DELETE FAILED</span>
                        <span v-else-if="doc.status === 'failed'" class="doc-badge failed">FAILED</span>
                        <span v-else>{{ formatDocDate(doc.uploaded_at) }}</span>
                      </td>
                      <td class="doc-size">{{ formatFileSize(doc.file_size) }}</td>
                      <td class="doc-actions">
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          @click="delete_company_doc(doc.doc_id)"
                          :disabled="!doc.doc_id || doc.status === 'uploading' || doc.status === 'processing' || doc.status === 'deleting' || docDeleting === doc.doc_id || deletingSelected"
                        >
                          <span v-if="docDeleting === doc.doc_id"><b-spinner small></b-spinner></span>
                          <span v-else>Delete</span>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-modal>

              <!-- Usage Details Modal -->
              <b-modal
                v-model="usageDetailsExpanded"
                title="Usage Details"
                size="lg"
                hide-footer
              >
                <div v-if="usageHistoryLoading" class="text-muted small" style="padding: 8px 0;">Loading usage history...</div>
                <div v-else-if="usageHistory.length === 0" class="text-muted small" style="padding: 8px 0;">No usage history.</div>
                <table v-else class="usage-history-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th style="text-align: right;">Cost</th>
                      <th style="text-align: right;">Chat Requests</th>
                      <th style="text-align: right;">Doc Uploads</th>
                      <th style="text-align: right;">Tokens (In / Out)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in usageHistory" :key="row.period" :class="{ 'row-current-month': row.is_current }">
                      <td class="history-period">
                        {{ formatHistoryPeriod(row.period) }}
                        <span v-if="row.is_current" class="history-current-badge">CURRENT</span>
                      </td>
                      <td class="history-cost">{{ row.blended_cost_dollars }}</td>
                      <td class="history-count">{{ row.chat_requests.toLocaleString() }}</td>
                      <td class="history-count">{{ row.doc_categorizations.toLocaleString() }}</td>
                      <td class="history-tokens">{{ formatTokenCount(row.input_tokens) }} / {{ formatTokenCount(row.output_tokens) }}</td>
                    </tr>
                  </tbody>
                  <tfoot v-if="usageHistory.length > 1">
                    <tr class="history-total-row">
                      <td style="font-weight: 700;">Total</td>
                      <td class="history-cost" style="font-weight: 700;">{{ usageHistoryTotalCostDollars }}</td>
                      <td class="history-count" style="font-weight: 700;">{{ usageHistoryTotalChatRequests.toLocaleString() }}</td>
                      <td class="history-count" style="font-weight: 700;">{{ usageHistoryTotalDocCategorizations.toLocaleString() }}</td>
                      <td class="history-tokens" style="font-weight: 700;">{{ formatTokenCount(usageHistoryTotalInputTokens) }} / {{ formatTokenCount(usageHistoryTotalOutputTokens) }}</td>
                    </tr>
                  </tfoot>
                </table>
                <p v-if="usageHistory.some(r => r.blended_cost_cents > 0 && r.chat_requests === 0 && r.doc_categorizations === 0)" class="usage-footnote">
                  * Periods showing cost with no chat or document activity reflect usage recorded before detailed activity tracking was enabled.
                </p>

                <!-- per-user breakdown -->
                <div class="user-breakdown-section">
                  <div class="user-breakdown-header">
                    <span class="user-breakdown-title">Per-User Breakdown</span>
                    <span v-if="userBreakdownPeriod" class="user-breakdown-period">{{ formatHistoryPeriod(userBreakdownPeriod) }}</span>
                  </div>
                  <div class="user-breakdown-period-links" v-if="usageHistory.length > 0">
                    <a
                      v-for="row in usageHistory"
                      :key="'ub-' + row.period"
                      href="#"
                      class="user-breakdown-period-link"
                      :class="{ active: userBreakdownPeriod === row.period }"
                      @click.prevent="selectBreakdownPeriod(row.period)"
                    >{{ formatHistoryPeriod(row.period) }}</a>
                  </div>
                  <div v-if="userBreakdownLoading" class="text-muted small" style="padding: 8px 0;">Loading per-user data...</div>
                  <div v-else-if="userBreakdown.length === 0" class="text-muted small" style="padding: 8px 0;">No per-user data for this period.</div>
                  <table v-else class="usage-history-table user-breakdown-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th style="text-align: right;">Cost</th>
                        <th style="text-align: right;">Chats</th>
                        <th style="text-align: right;">Doc Uploads</th>
                        <th style="text-align: right;">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in userBreakdown" :key="row.user_id">
                        <td class="user-breakdown-user">{{ row.user_id }}</td>
                        <td class="history-cost">{{ row.blended_cost_dollars }}</td>
                        <td class="history-count">{{ row.chat_requests.toLocaleString() }}</td>
                        <td class="history-count">{{ row.doc_categorizations.toLocaleString() }}</td>
                        <td class="history-tokens">{{ formatTokenCount(row.input_tokens + row.output_tokens) }}</td>
                      </tr>
                      <tr v-if="userBreakdownUnattributed && userBreakdownUnattributed.cost > 0" class="user-breakdown-unattributed">
                        <td class="user-breakdown-user" style="font-style: italic; color: #999;">Unattributed</td>
                        <td class="history-cost">${{ (userBreakdownUnattributed.cost / 100).toFixed(2) }}</td>
                        <td class="history-count">{{ userBreakdownUnattributed.chats.toLocaleString() }}</td>
                        <td class="history-count">{{ userBreakdownUnattributed.docs.toLocaleString() }}</td>
                        <td class="history-tokens">{{ formatTokenCount(userBreakdownUnattributed.tokens) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-modal>
            </td>
          </tr>

        </table>


      </form>
    </div>
    <div v-else>
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

  <div class="tc-sticky-save">
    <transition name="tc-save-slide">
    <div v-if="isDirty || saving" class="d-flex align-items-center justify-content-center">
      <span v-if="isDirty && !saving" class="tc-unsaved-label mr-3" @click="scrollToFirstDirtyField()" title="Click to scroll to unsaved field" style="cursor:pointer">&#9679; Unsaved changes</span>
      <b-btn size="lg" variant="success" @click="save_config" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </b-btn>
      &nbsp;&nbsp;
      <b-btn size="lg" variant="secondary" @click="load_data(true)">Discard Changes</b-btn>
    </div>
    </transition>
  </div>
</div>

</template>

<script>
import { get_tenant_config_settings, update_tenant_config_settings, get_chat_instructions, list_fa_documents_company, presign_fa_document_company, finalize_fa_document_company, delete_fa_document_company, get_fa_documents_summary, purge_fa_documents_user, get_ai_usage, reset_ai_usage, get_ai_usage_history, get_ai_usage_users } from '../networking/apis'
import { tbn_get_chat_instructions } from '../networking/tbn_apis'
import {mapState} from "vuex";
import dirtyTrackingMixin from '../mixins/dirty_tracking_mixin'
import {
  DotsAnimation,
  SpinnerAnimation,
  VanAnimation,
  CoachBusAnimation,
  SchoolBusAnimation,
  ShuttleAnimation,
  LimoAnimation,
  TaxiAnimation,
  animationComponents
} from './animations'

export default {
  name: 'tenant_config_fleet_assistant',
  mixins: [dirtyTrackingMixin],
  components: {
    DotsAnimation,
    SpinnerAnimation,
    VanAnimation,
    CoachBusAnimation,
    SchoolBusAnimation,
    ShuttleAnimation,
    LimoAnimation,
    TaxiAnimation
  },
  data() {
    return {
      group_name: 'general',
      model: {
        fleet_assistant_enabled: 'True',
        openai_model: 'gpt-5.4',
        openai_doc_model: 'gpt-4.1-nano',
        openai_api_key: '',
        fleet_assistant_animation: 'dots',
        fleet_assistant_instructions: '',
        ai_cost_limit_cents: 2000,
        user_document_uploads_enabled: 'True',
        company_documents_in_help_center: 'True',
        company_documents_in_fleet_assistant: 'True',
      },
      boolean_options: [
        { text: 'True', value: 'True' },
        { text: 'False', value: 'False' },
      ],
      usagePercent: 0,
      usagePeriod: '',
      usageOverLimit: false,
      usageChatPercent: 0,
      usageDocsPercent: 0,
      usageLimitDollars: '20',
      resettingUsage: false,
      defaultInstructions: 'Loading instructions...',
      loading: true,
      openai_model_options: [
        { text: 'GPT-4o mini - legacy, affordable', value: 'gpt-4o-mini' },
        { text: 'GPT-4.1 mini - fast, 1M context', value: 'gpt-4.1-mini' },
        { text: 'GPT-4.1 - smart, 1M context', value: 'gpt-4.1' },
        { text: 'GPT-5 mini - fast GPT-5', value: 'gpt-5-mini' },
        { text: 'GPT-5.2 - capable reasoning', value: 'gpt-5.2' },
        { text: 'GPT-5.4 mini - fast flagship', value: 'gpt-5.4-mini' },
        { text: 'GPT-5.4 - most capable (default)', value: 'gpt-5.4' },
      ],
      openai_doc_model_options: [
        { text: 'GPT-4.1 nano - cheapest (default)', value: 'gpt-4.1-nano' },
        { text: 'GPT-4o mini - legacy, reliable', value: 'gpt-4o-mini' },
        { text: 'GPT-4.1 mini - fast, 1M context', value: 'gpt-4.1-mini' },
        { text: 'GPT-5 nano - cheap GPT-5', value: 'gpt-5-nano' },
        { text: 'GPT-5.4 nano - cheap flagship', value: 'gpt-5.4-nano' },
        { text: 'GPT-5.4 mini', value: 'gpt-5.4-mini' },
      ],
      animation_options: [
        { text: 'Typing Dots', value: 'dots' },
        { text: 'Van', value: 'van' },
        { text: 'Coach Bus', value: 'coach' },
        { text: 'School Bus', value: 'school' },
        { text: 'Shuttle', value: 'shuttle' },
        { text: 'Limousine', value: 'limo' },
        { text: 'Taxi', value: 'taxi' },
        { text: 'Thinking...', value: 'spinner' },
      ],
      companyDocs: [],
      companyDocsLoading: false,
      companyDocsError: '',
      companyUploading: false,
      docDeleting: null,
      // Document stores
      docStores: [],
      docStoresLoading: false,
      showCompanyDocModal: false,
      purgingUser: null,
      // Multi-select for company docs
      selectedDocs: [],
      deletingSelected: false,
      deleteProgress: '',
      _companyDocsPollTimer: null,
      _companyDocsPollDelay: 2000,
      _docStoresPollTimer: null,
      _docStoresPollDelay: 2000,
      // usage details expander (persists across tenant switches)
      usageDetailsExpanded: false,
      usageHistory: [],
      usageHistoryLoading: false,
      userBreakdown: [],
      userBreakdownLoading: false,
      userBreakdownPeriod: '',
    }
  },
  async mounted() {
    this.load_data()
  },
  beforeDestroy() {
    this.stopCompanyDocsRefresh()
    this.stopDocStoresRefresh()
  },
  watch: {
    'user.tenant'() {
      this.load_data()
    }
  },
  methods: {
    normalizeCostLimitCents(value) {
      const parsed = parseInt(value, 10)
      // treat truly-unset as default; preserve 0 (admin disabled AI for this tenant)
      if (value === null || value === undefined || value === '') return 2000
      if (Number.isNaN(parsed) || parsed < 0) return 0
      return parsed
    },
    normalizeUsagePercent(value) {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed) || parsed < 0) return 0
      if (parsed > 100) return 100
      return parsed
    },
    normalizeUsageSplitPercent(value) {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed) || parsed < 0) return 0
      if (parsed > 100) return 100
      return parsed
    },
    openUsageDetails() {
      this.usageDetailsExpanded = true
      if (this.usageHistory.length === 0) {
        this.load_usage_history()
      }
      if (this.userBreakdown.length === 0) {
        this.load_user_breakdown()
      }
    },
    async load_usage_history() {
      this.usageHistoryLoading = true
      try {
        const { value: { data = {} } } = await get_ai_usage_history(this.user.tenant, 12)
        const result = data.data || data
        const now = new Date()
        const currentPeriod = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
        this.usageHistory = (result.months || []).map(row => ({
          ...row,
          is_current: row.period === currentPeriod,
        }))
      } catch (e) {
        console.warn('Failed to load usage history:', e)
        this.usageHistory = []
      } finally {
        this.usageHistoryLoading = false
      }
    },
    async load_user_breakdown(period) {
      this.userBreakdownLoading = true
      try {
        const { value: { data = {} } } = await get_ai_usage_users(this.user.tenant, period || '')
        const result = data.data || data
        this.userBreakdown = result.users || []
        this.userBreakdownPeriod = result.period || period || ''
      } catch (e) {
        console.warn('Failed to load user breakdown:', e)
        this.userBreakdown = []
      } finally {
        this.userBreakdownLoading = false
      }
    },
    selectBreakdownPeriod(period) {
      this.load_user_breakdown(period)
    },
    formatHistoryPeriod(period) {
      if (!period || !/^\d{4}-\d{2}$/.test(period)) return period || ''
      const [year, month] = period.split('-')
      const date = new Date(Number(year), Number(month) - 1, 1)
      if (Number.isNaN(date.getTime())) return period
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    },
    formatTokenCount(count) {
      if (!count || count === 0) return '0'
      if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
      if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
      return count.toLocaleString()
    },
    async load_usage_summary() {
      try {
        const { value: { data = {} } } = await get_ai_usage(this.user.tenant)
        const usage = data.data || data
        this.usagePercent = this.normalizeUsagePercent(usage.percentage)
        this.usagePeriod = usage.period || ''
        this.usageOverLimit = !!usage.over_limit
        this.usageChatPercent = this.normalizeUsageSplitPercent(usage.chat_percentage)
        this.usageDocsPercent = this.normalizeUsageSplitPercent(usage.docs_percentage)
      } catch (e) {
        this.usagePercent = 0
        this.usagePeriod = ''
        this.usageOverLimit = false
        this.usageChatPercent = 0
        this.usageDocsPercent = 0
      }
    },
    async reset_usage_stats() {
      if (!this.isAdmin) return
      const periodLabel = this.usagePeriodLabel || 'the current month'
      const confirmed = confirm(
        `Reset Fleet Assistant usage stats for ${periodLabel}? This will clear the current month counters for this tenant.`
      )
      if (!confirmed) return

      this.resettingUsage = true
      try {
        const { value: { data = {} } } = await reset_ai_usage(this.user.tenant)
        const result = data.data || data
        await Promise.all([this.load_usage_summary(), this.load_usage_history()])
        const deletedRows = parseInt(result.deleted_rows, 10) || 0
        this.$emit(
          'showAlert',
          `Usage stats reset. Cleared ${deletedRows} record${deletedRows === 1 ? '' : 's'}.`,
          'success',
          5000
        )
      } catch (e) {
        const errorMessage = e && e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : 'Failed to reset usage stats'
        this.$emit('showAlert', errorMessage, 'danger', 5000)
      } finally {
        this.resettingUsage = false
      }
    },
    async load_data(silent = false) {
      if (!silent) this.loading = true
      try {
        const {value: { data = [] } } = await get_tenant_config_settings(this.user.tenant)
        const results = data.results || {}
        
        // merge loaded data with defaults (preserves reactivity)
        this.model.fleet_assistant_enabled = results.fleet_assistant_enabled ? 'True' : 'False'
        // migrate old default to current flagship
        const chatModel = results.openai_model || 'gpt-5.4'
        this.model.openai_model = chatModel === 'gpt-5.2' ? 'gpt-5.4' : chatModel
        const docModel = results.openai_doc_model || 'gpt-4.1-nano'
        this.model.openai_doc_model = docModel === 'gpt-4o-mini' ? 'gpt-4.1-nano' : docModel
        this.model.openai_api_key = results.openai_api_key || ''
        this.model.fleet_assistant_animation = results.fleet_assistant_animation || 'dots'
        this.model.fleet_assistant_instructions = results.fleet_assistant_instructions || ''
        this.model.ai_cost_limit_cents = this.normalizeCostLimitCents(results.ai_cost_limit_cents)
        this.usageLimitDollars = String(Math.round(this.model.ai_cost_limit_cents / 100))
        this.model.user_document_uploads_enabled = results.user_document_uploads_enabled === false ? 'False' : 'True'
        this.model.company_documents_in_help_center = results.company_documents_in_help_center === false ? 'False' : 'True'
        this.model.company_documents_in_fleet_assistant = results.company_documents_in_fleet_assistant === false ? 'False' : 'True'

        // fetch default instructions from backend (use TBN or CM endpoint based on tenant)
        try {
          const isTbnTenant = this.user.tenant && this.user.tenant.includes('-')
          const instructionsResponse = isTbnTenant
            ? await tbn_get_chat_instructions(this.user.tenant)
            : await get_chat_instructions(this.user.tenant)
          
          const instructions = instructionsResponse &&
            instructionsResponse.value &&
            instructionsResponse.value.data &&
            instructionsResponse.value.data.instructions
          if (instructions) {
            this.defaultInstructions = instructions
          }
        } catch (instructionsError) {
          console.warn('Failed to load default instructions:', instructionsError)
          this.defaultInstructions = 'Unable to load default instructions'
        }

        await Promise.all([
          this.load_doc_stores(),
          this.load_usage_summary(),
          this.isAdmin ? this.load_usage_history() : Promise.resolve(),
        ])
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
      this._resetDirtyTracking()
    },
    async load_doc_stores(silent = false) {
      if (!silent) {
        this.docStoresLoading = true
      }
      try {
        const { value: { data = {} } } = await get_fa_documents_summary(this.user.tenant)
        const results = data.data || data
        this.docStores = results.stores || []
        this.scheduleDocStoresRefresh()
      } catch (e) {
        console.warn('Failed to load document stores:', e)
        if (!silent) {
          this.docStores = []
        }
      } finally {
        if (!silent) {
          this.docStoresLoading = false
        }
      }
    },
    async load_company_docs(silent = false) {
      if (!silent) {
        this.companyDocsError = ''
        this.companyDocsLoading = true
      }
      try {
        const { value: { data = {} } } = await list_fa_documents_company(this.user.tenant)
        const results = data.data || data
        const serverDocs = results.documents || []

        const existing = this.companyDocs || []
        const existingByDocId = new Map()
        for (const doc of existing) {
          if (doc && doc.doc_id) existingByDocId.set(doc.doc_id, doc)
        }

        const merged = []
        for (const doc of serverDocs) {
          const current = existingByDocId.get(doc.doc_id)
          if (current) {
            Object.assign(current, doc)
            merged.push(current)
          } else {
            merged.push({ ...doc })
          }
        }

        const serverDocIds = new Set(serverDocs.map(d => d.doc_id).filter(Boolean))
        const localInProgress = existing.filter(d =>
          (d.status === 'uploading' || d.status === 'processing' || d.status === 'failed') &&
          (!d.doc_id || !serverDocIds.has(d.doc_id))
        )

        const combined = [...merged, ...localInProgress]
        this.companyDocs = combined.sort((a, b) => (b.uploaded_at || 0) - (a.uploaded_at || 0))
        const validIds = new Set(this.companyDocs.map(d => d.doc_id).filter(Boolean))
        this.selectedDocs = this.selectedDocs.filter(id => validIds.has(id))
        this.scheduleCompanyDocsRefresh()
      } catch (e) {
        console.warn('Failed to load company documents:', e)
        if (!silent) {
          this.companyDocsError = e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Could not load company documents'
          this.companyDocs = (this.companyDocs || []).filter(d =>
            d.status === 'uploading' || d.status === 'processing' || d.status === 'failed'
          )
        }
      } finally {
        if (!silent) {
          this.companyDocsLoading = false
        }
      }
    },
    onCompanyDocPicked(event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.upload_company_docs(Array.from(files))
      }
      // Reset picker so same file can be selected again
      if (this.$refs.companyDocPicker) {
        this.$refs.companyDocPicker.value = ''
      }
    },
    async upload_company_docs(files) {
      if (!files || files.length === 0) return
      this.companyUploading = true
      this.companyDocsError = ''
      
      let successCount = 0
      let failCount = 0
      const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB
      const MAX_DOCS = 25
      const currentCount = (this.companyDocs || []).filter(d =>
        d.doc_id || d.status === 'uploading' || d.status === 'processing'
      ).length
      const CONCURRENCY = 3
      
      // validate files and create placeholders upfront
      const items = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.size > MAX_FILE_SIZE) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
          console.warn(`File too large: ${file.name}`)
          failCount++
          this.companyDocsError = `${file.name} is ${sizeMB} MB — maximum is 25 MB`
          continue
        }
        if (currentCount + items.length >= MAX_DOCS) {
          console.warn('Company document limit reached')
          failCount++
          this.companyDocsError = `Document limit reached (${MAX_DOCS}). Delete a document to add another.`
          break
        }
        const tempId = `temp-${Date.now()}-${i}-${Math.random().toString(16).slice(2)}`
        const placeholder = {
          temp_id: tempId,
          doc_id: null,
          display_name: file.name,
          uploaded_by: (this.user && (this.user.email || this.user.username)) || '—',
          uploaded_at: Math.floor(Date.now() / 1000),
          file_size: file.size,
          status: 'uploading',
        }
        this.companyDocs = [placeholder, ...(this.companyDocs || [])]
        items.push({ file, tempId })
      }
      
      // worker pool: 3 workers drain the queue, finalize fires in background
      const finalizePromises = []
      let index = 0

      const worker = async () => {
        while (index < items.length) {
          const { file, tempId } = items[index++]
          const ok = await this._uploadCompanyItem(file, tempId, finalizePromises)
          if (ok) successCount++
          else failCount++
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker())
      )
      // wait for trailing finalize calls to settle
      const finalizeResults = await Promise.allSettled(finalizePromises)
      for (const r of finalizeResults) {
        if (r.status === 'rejected' || (r.status === 'fulfilled' && !r.value)) {
          // upload succeeded but finalize failed — already counted as success above,
          // bump fail and decrement success to correct the counts
          failCount++
          successCount = Math.max(0, successCount - 1)
        }
      }
      
      await this.load_company_docs()
      this.companyUploading = false
      
      if (failCount === 0) {
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Uploaded ${successCount} document(s)`, 'success', 3000)
      } else if (successCount === 0) {
        this.companyDocsError = 'Upload failed'
        this.$emit('showAlert', 'Upload failed', 'danger', 5000)
      } else {
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Uploaded ${successCount}, failed ${failCount}`, 'warning', 5000)
      }
    },
    // presign + S3 upload, then kick off finalize in the background
    async _uploadCompanyItem(file, tempId, finalizePromises) {
      try {
        const filename = file && file.name ? file.name : 'document'
        const contentType = file && file.type ? file.type : 'application/octet-stream'
        const presignResp = await presign_fa_document_company(this.user.tenant, {
          filename,
          content_type: contentType,
          file_size: file && file.size ? file.size : 0,
          display_name: filename,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data ? (presignResp.value.data.data || presignResp.value.data) : {}
        const upload = presignData.upload || {}
        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)
        const s3Resp = await fetch(upload.url, { method: 'POST', body: uploadForm })
        if (!s3Resp.ok) {
          throw new Error('S3 upload failed')
        }
        // S3 upload done — mark as processing and fire off finalize in background
        this.companyDocs = (this.companyDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'processing', doc_id: presignData.doc_id } : d)
        finalizePromises.push(this._finalizeCompanyItem(tempId, presignData, filename, contentType))
        return true
      } catch (e) {
        console.warn(`Failed to upload ${file.name}:`, e)
        this.companyDocs = (this.companyDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'failed' } : d)
        return false
      }
    },
    async _finalizeCompanyItem(tempId, presignData, filename, contentType) {
      try {
        const finalizeResp = await finalize_fa_document_company(this.user.tenant, {
          doc_id: presignData.doc_id,
          filename,
          content_type: contentType,
          display_name: presignData.display_name || filename,
        })
        const finalized = finalizeResp && finalizeResp.value && finalizeResp.value.data ? (finalizeResp.value.data.data || finalizeResp.value.data) : {}
        this.companyDocs = (this.companyDocs || []).map(d => {
          if (d.temp_id !== tempId) return d
          return {
            ...d,
            doc_id: finalized.doc_id || presignData.doc_id || d.doc_id,
            display_name: finalized.display_name || presignData.display_name || d.display_name,
            uploaded_at: finalized.uploaded_at || d.uploaded_at,
            uploaded_by: finalized.uploaded_by || d.uploaded_by,
            file_size: finalized.file_size || d.file_size,
            status: undefined,
          }
        })
        return true
      } catch (e) {
        console.warn(`Failed to finalize ${filename}:`, e)
        this.companyDocs = (this.companyDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'failed' } : d)
        return false
      }
    },
    formatDocDate(timestamp) {
      if (!timestamp) return '—'
      const d = new Date(timestamp * 1000)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '—'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    async delete_company_doc(docId) {
      if (!confirm('Delete this document? The assistant will no longer use it.')) return
      this.docDeleting = docId
      try {
        await delete_fa_document_company(this.user.tenant, docId)
        await this.load_company_docs()
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', 'Document deleted', 'success', 3000)
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed', 'danger', 5000)
      } finally {
        this.docDeleting = null
      }
    },
    scheduleCompanyDocsRefresh() {
      if (!this.showCompanyDocModal) {
        this.stopCompanyDocsRefresh()
        return
      }
      const hasProcessing = (this.companyDocs || []).some(d => (d.status || 'uploaded') === 'processing')
      if (!hasProcessing) {
        this.stopCompanyDocsRefresh()
        return
      }
      if (this._companyDocsPollTimer) return
      const delay = this._companyDocsPollDelay || 2000
      this._companyDocsPollTimer = setTimeout(async () => {
        this._companyDocsPollTimer = null
        if (this.companyDocsLoading) {
          this.scheduleCompanyDocsRefresh()
          return
        }
        await this.load_company_docs(true)
        this._companyDocsPollDelay = Math.min(Math.round((this._companyDocsPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopCompanyDocsRefresh() {
      if (this._companyDocsPollTimer) {
        clearTimeout(this._companyDocsPollTimer)
        this._companyDocsPollTimer = null
      }
      this._companyDocsPollDelay = 2000
    },
    scheduleDocStoresRefresh() {
      const serverProcessing = (this.docStores || []).some(s => (s.processing_count || 0) > 0)
      const localInFlight = (this.companyDocs || []).some(d => d.status === 'uploading' || d.status === 'processing')
      if (!serverProcessing && !localInFlight) {
        this.stopDocStoresRefresh()
        return
      }
      if (this._docStoresPollTimer) return
      const delay = this._docStoresPollDelay || 2000
      this._docStoresPollTimer = setTimeout(async () => {
        this._docStoresPollTimer = null
        if (this.docStoresLoading) {
          this.scheduleDocStoresRefresh()
          return
        }
        await this.load_doc_stores(true)
        this._docStoresPollDelay = Math.min(Math.round((this._docStoresPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopDocStoresRefresh() {
      if (this._docStoresPollTimer) {
        clearTimeout(this._docStoresPollTimer)
        this._docStoresPollTimer = null
      }
      this._docStoresPollDelay = 2000
    },
    async openCompanyDocModal() {
      this.showCompanyDocModal = true
      await this.load_company_docs()
    },
    onCompanyDocModalClosed() {
      this.selectedDocs = []
      this.stopCompanyDocsRefresh()
      this.load_doc_stores()
    },
    async purge_user_docs(userId) {
      const store = this.docStores.find(s => s.owner === userId)
      const count = store ? store.doc_count : 0
      if (!confirm(`Delete ALL ${count} document(s) for user "${userId}"? This cannot be undone.`)) return
      this.purgingUser = userId
      try {
        const { value: { data = {} } } = await purge_fa_documents_user(this.user.tenant, userId)
        const results = data.data || data
        const deletedCount = results.deleted_count || 0
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Purged ${deletedCount} document(s) for ${userId}`, 'success', 3000)
        await this.load_doc_stores()
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Purge failed', 'danger', 5000)
      } finally {
        this.purgingUser = null
      }
    },
    toggleDocSelection(docId) {
      if (!docId) return
      const idx = this.selectedDocs.indexOf(docId)
      if (idx === -1) {
        this.selectedDocs.push(docId)
      } else {
        this.selectedDocs.splice(idx, 1)
      }
    },
    toggleSelectAll() {
      if (this.allDocsSelected) {
        this.selectedDocs = []
      } else {
        this.selectedDocs = this.selectableCompanyDocs.map(d => d.doc_id)
      }
    },
    async delete_selected_docs() {
      const toDelete = [...this.selectedDocs]
      const count = toDelete.length
      if (count === 0) return
      if (!confirm(`Delete ${count} selected document(s)? This cannot be undone.`)) return
      
      this.deletingSelected = true
      let completed = 0
      let successCount = 0
      let failCount = 0
      this.deleteProgress = `0/${count}`
      
      // mark all selected rows as deleting upfront
      const deleteSet = new Set(toDelete)
      this.companyDocs = this.companyDocs.map(d => deleteSet.has(d.doc_id) ? { ...d, status: 'deleting' } : d)
      
      const CONCURRENCY = 3
      let index = 0
      
      const worker = async () => {
        while (index < toDelete.length) {
          const docId = toDelete[index++]
          try {
            await delete_fa_document_company(this.user.tenant, docId)
            successCount++
            // remove from list immediately on success
            this.companyDocs = (this.companyDocs || []).filter(d => d.doc_id !== docId)
          } catch (e) {
            console.warn(`Failed to delete doc ${docId}:`, e)
            failCount++
            this.companyDocs = (this.companyDocs || []).map(d => d.doc_id === docId ? { ...d, status: 'delete-failed' } : d)
          }
          completed++
          this.deleteProgress = `${completed}/${count}`
        }
      }
      
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, toDelete.length) }, () => worker())
      )
      
      this.selectedDocs = []
      await this.load_company_docs()
      this.deletingSelected = false
      this.deleteProgress = ''
      
      if (failCount === 0) {
        this.$emit('showAlert', `Deleted ${successCount} document(s)`, 'success', 3000)
      } else {
        this.$emit('showAlert', `Deleted ${successCount}, failed ${failCount}`, 'warning', 5000)
      }
    },
    async save_config() {
      // validate custom instructions length
      if (this.model.fleet_assistant_instructions && this.model.fleet_assistant_instructions.length > 2000) {
        this.$emit('showAlert', 'Custom instructions must be 2000 characters or less', 'warning', 5000)
        return
      }

      if (this.canEditCostLimit) {
        const dollars = parseInt(this.usageLimitDollars, 10)
        if (Number.isNaN(dollars) || dollars < 0) {
          this.$emit('showAlert', 'Monthly Cost Limit must be a whole dollar amount of $0 or more', 'warning', 5000)
          return
        }
        this.model.ai_cost_limit_cents = dollars * 100
      }
      
      try {
        this.saving = true
        this.$emit('showAlert', 'Saving Changes', 'info', 5000)
        await update_tenant_config_settings(this.user.tenant, this.model, this.group_name)
        this._resetDirtyTracking()
        this.load_data(true)
        this.$root.$emit('fa-settings-changed')
        this.$emit('showAlert', 'Configuration Saved', 'success', 5000)
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      finally {
        this.saving = false
      }
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    isAdmin() {
      return (this.user && this.user.role) === 'admin'
    },
    canEditCostLimit() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin'
    },
    docStoresForDisplay() {
      if (this.docStores && this.docStores.length > 0) {
        return this.docStores
      }
      return [{ owner: 'company', doc_count: 0, total_size: 0, processing_count: 0 }]
    },
    usageSplitNormalized() {
      if (this.usagePercent <= 0) {
        return { chat: 0, docs: 0 }
      }
      const chat = this.normalizeUsageSplitPercent(this.usageChatPercent)
      const docs = this.normalizeUsageSplitPercent(this.usageDocsPercent)
      const total = chat + docs
      if (total <= 0) {
        // Fallback for legacy rows where split data is unavailable.
        return { chat: 100, docs: 0 }
      }
      return {
        chat: (chat / total) * 100,
        docs: (docs / total) * 100,
      }
    },
    usageChatBarValue() {
      return (this.usagePercent * this.usageSplitNormalized.chat) / 100
    },
    usageDocsBarValue() {
      return (this.usagePercent * this.usageSplitNormalized.docs) / 100
    },
    usageChatMonthPercent() {
      return this.normalizeUsagePercent(Math.round(this.usageChatBarValue))
    },
    usageDocsMonthPercent() {
      return this.normalizeUsagePercent(Math.max(0, this.usagePercent - this.usageChatMonthPercent))
    },
    usageFreePercent() {
      return Math.max(0, 100 - this.usagePercent)
    },
    usageRemainingDisplay() {
      return this.normalizeUsagePercent(Math.round(this.usageFreePercent))
    },
    usageNearLimit() {
      return this.usagePercent >= 80
    },
    usageBarVariant() {
      return this.usageNearLimit ? 'warning' : 'primary'
    },
    usageRemainingBarValue() {
      return this.usageFreePercent
    },
    usagePeriodLabel() {
      if (!this.usagePeriod || !/^\d{4}-\d{2}$/.test(this.usagePeriod)) return ''
      const [year, month] = this.usagePeriod.split('-')
      const date = new Date(Number(year), Number(month) - 1, 1)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    },
    currentAnimationComponent() {
      return animationComponents[this.model.fleet_assistant_animation] || 'DotsAnimation'
    },
    currentMonthUsage() {
      return this.usageHistory.find(r => r.is_current) || null
    },
    userBreakdownUnattributed() {
      if (!this.userBreakdownPeriod || this.userBreakdown.length === 0) return null
      const tenantRow = this.usageHistory.find(r => r.period === this.userBreakdownPeriod)
      if (!tenantRow) return null
      const userCost = this.userBreakdown.reduce((s, r) => s + (r.blended_cost_cents || 0), 0)
      const userChats = this.userBreakdown.reduce((s, r) => s + (r.chat_requests || 0), 0)
      const userDocs = this.userBreakdown.reduce((s, r) => s + (r.doc_categorizations || 0), 0)
      const userTokens = this.userBreakdown.reduce((s, r) => s + (r.input_tokens || 0) + (r.output_tokens || 0), 0)
      const tenantTokens = (tenantRow.input_tokens || 0) + (tenantRow.output_tokens || 0)
      return {
        cost: Math.max(0, (tenantRow.blended_cost_cents || 0) - userCost),
        chats: Math.max(0, (tenantRow.chat_requests || 0) - userChats),
        docs: Math.max(0, (tenantRow.doc_categorizations || 0) - userDocs),
        tokens: Math.max(0, tenantTokens - userTokens),
      }
    },
    allDocsSelected() {
      return this.selectableCompanyDocs.length > 0 && this.selectedDocs.length === this.selectableCompanyDocs.length
    },
    someDocsSelected() {
      return this.selectedDocs.length > 0
    },
    selectableCompanyDocs() {
      return (this.companyDocs || []).filter(d =>
        d.doc_id &&
        d.status !== 'uploading' &&
        d.status !== 'processing' &&
        d.status !== 'deleting'
      )
    },
    companyDocsTotalSize() {
      return this.companyDocs.reduce((sum, doc) => sum + (doc.file_size || 0), 0)
    },
    usageHistoryTotalCostDollars() {
      const cents = this.usageHistory.reduce((sum, r) => sum + (r.blended_cost_cents || 0), 0)
      return `$${(cents / 100).toFixed(2)}`
    },
    usageHistoryTotalChatRequests() {
      return this.usageHistory.reduce((sum, r) => sum + (r.chat_requests || 0), 0)
    },
    usageHistoryTotalDocCategorizations() {
      return this.usageHistory.reduce((sum, r) => sum + (r.doc_categorizations || 0), 0)
    },
    usageHistoryTotalInputTokens() {
      return this.usageHistory.reduce((sum, r) => sum + (r.input_tokens || 0), 0)
    },
    usageHistoryTotalOutputTokens() {
      return this.usageHistory.reduce((sum, r) => sum + (r.output_tokens || 0), 0)
    }
  }
}
</script>

<style scoped>
/* sys-admin rows: subtle orange left border instead of loud <span>Admin</span>
   badges. badges were noisy and — critically — did nothing functional (rows
   still had to be v-if'd out of the DOM). this class is purely a visual cue
   for the admin themselves; non-admins never see these rows thanks to
   v-if="isAdmin" on every .admin-only-row. */
.admin-only-row td {
  background-color: #fff8ef;
}
.admin-only-row td:first-child {
  box-shadow: inset 4px 0 0 0 #f0a957;
  padding-left: 14px !important;
}

/* stacked row: label on top, textarea in middle, help at bottom —
   used for long-form inputs like Custom Instructions where the
   3-column label | control | help pattern leaves the control
   cramped. matches desktop and mobile. */
.fa-stacked-cell {
  /* match cellpadding="10" horizontal inset used by other rows so the
     stacked block's left/right edges line up with the 3-column rows
     above and below it. */
  padding: 4px 10px !important;
}
.fa-stacked-label {
  /* inherit font-size from the table so the stacked label reads at
     the same scale as the 3-column labels around it (18px desktop,
     16px mobile via the media-query override). */
  font-weight: bold;
  color: #333;
  text-align: left;
  margin-bottom: 6px;
}
.fa-stacked-textarea {
  width: 100%;
  font-size: 14px;
  min-height: 180px;
  field-sizing: content;
}
.fa-stacked-charcount {
  font-size: 11px;
  color: #888;
  margin-top: 4px;
  text-align: right;
}
.fa-stacked-help {
  font-size: 12px;
  color: #888;
  font-weight: normal;
  margin-top: 8px;
}

/* bootstrap input-group ($ prepend + number input) has to stay
   side-by-side even when my mobile rule forces .form-control to
   100%. .form-control inside an input-group uses flex: 1 1 auto
   with width: 1% (bootstrap's idiom) so the prepend addon holds
   its width and the input fills the remainder. */
.fa-settings-table ::v-deep tr > td .input-group .form-control {
  width: 1% !important;
  max-width: none !important;
  flex: 1 1 auto;
}

/* narrow viewports: collapse the 3-column table into a 2-column grid per
   row. label stays left; control stretches across old columns 2+3; help
   text drops underneath the control. matches the user_detail mobile
   pattern so settings pages read the same on phones. */
/* ::v-deep so the selectors reach the browser-auto-inserted <tbody>, which
   doesn't inherit the component's scoped [data-v-...] attribute. without
   deep, display: block on tbody never applies and the table keeps using
   table-layout, sizing to the widest content and overflowing the viewport
   on narrow screens. */
@media (max-width: 900px) {
  .fa-settings-table {
    display: block;
    width: 100% !important;
    font-size: 16px !important;
    box-sizing: border-box;
  }
  .fa-settings-table ::v-deep tbody {
    display: block;
    width: 100%;
  }
  .fa-settings-table ::v-deep tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    column-gap: 4px;
    width: 100%;
    box-sizing: border-box;
    /* position:relative anchors the absolutely-positioned label cell
       (see below) so a tall wrapped label doesn't expand the grid
       rows and leave a gap between the input and its help text. */
    position: relative;
    /* visual breathing room between one setting's help text and the
       next setting's label/control. tr + tr to avoid adding a gap
       above the very first row. */
  }
  .fa-settings-table ::v-deep tr + tr {
    margin-top: 10px;
  }
  .fa-settings-table ::v-deep tr > td {
    min-width: 0;
    width: auto !important;
    overflow-wrap: break-word;
    padding-top: 3px !important;
    padding-bottom: 3px !important;
  }
  .fa-settings-table ::v-deep tr > td[colspan="3"] {
    grid-column: 1 / -1;
  }
  /* label: absolutely positioned in col 1 so its height doesn't affect
     the grid rows that hold the input (row 1) and help (row 2). this
     keeps the input and help stacked tightly even when the label wraps
     to three lines (e.g. "Document categorization model"). the gold
     accent stripe is a pseudo-element at the label's right edge so
     it naturally matches the label's own rendered height. */
  .fa-settings-table ::v-deep tr > td[width="400"] {
    position: absolute;
    left: 0;
    top: 0;
    width: calc((100% / 3) - 4px) !important;
    white-space: normal;
  }
  .fa-settings-table ::v-deep tr > td[width="500"] {
    grid-column: 2;
    grid-row: 1;
  }
  .fa-settings-table ::v-deep tr > td[width="350"] {
    grid-column: 2;
    grid-row: 2;
    padding-top: 2px;
  }
  .fa-settings-table ::v-deep tr > td .custom-select,
  .fa-settings-table ::v-deep tr > td select,
  .fa-settings-table ::v-deep tr > td input[type="text"],
  .fa-settings-table ::v-deep tr > td input[type="password"],
  .fa-settings-table ::v-deep tr > td input[type="number"],
  .fa-settings-table ::v-deep tr > td textarea,
  .fa-settings-table ::v-deep tr > td .form-control,
  .fa-settings-table ::v-deep tr > td .input-group {
    width: 100% !important;
    max-width: 100% !important;
  }
  /* admin-only tone on mobile: drop the whole-row cream tint (it
     was visually heavy on a narrow phone) and keep only the label
     cell tinted with its orange right-edge stripe. the stripe +
     tinted label are now the sole signal that a setting is
     admin-only. */
  .fa-settings-table ::v-deep .admin-only-row {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  .fa-settings-table ::v-deep .admin-only-row td {
    background-color: transparent !important;
  }
  .fa-settings-table ::v-deep .admin-only-row td[width="400"] {
    background-color: #fff8ef !important;
    box-shadow: inset -4px 0 0 0 #f0a957 !important;
    padding-right: 10px !important;
    padding-left: 10px !important;
  }
  /* neutralize the desktop admin-only left stripe + padding on
     section-title rows so "AI Model" aligns with "Appearance". */
  .fa-settings-table ::v-deep .admin-only-row td[colspan="3"] {
    box-shadow: none !important;
    padding-left: 10px !important;
  }
}

.animation-preview {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px 20px;
  display: inline-block;
}

.usage-quota-wrap {
  width: 100%;
}

.usage-progress {
  width: 100%;
  border: 1px solid #d9d9d9;
}

.usage-meta {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.usage-period {
  color: #777;
}

.usage-breakdown-meta {
  justify-content: flex-start;
  gap: 16px;
}

.usage-period-right {
  margin-left: auto;
  text-align: right;
}

.usage-breakdown-item {
  font-weight: 600;
  color: #4b5563;
}

.usage-breakdown-item-chat {
  color: #218838;
}

.usage-breakdown-item-docs {
  color: #1f5fbf;
}

.usage-quota-used,
.usage-quota-remaining {
  font-weight: 600;
  color: #4b5563;
}

.usage-near-limit-cta {
  margin-top: 8px;
  font-size: 13px;
  color: #92400e;
}

.usage-near-limit-cta a {
  font-weight: 600;
  color: #b45309;
}

.usage-near-limit-cta a:hover {
  text-decoration: underline;
}

.usage-remaining-bar {
  background-color: #e9ecef !important;
}

.row-uploading {
  opacity: 0.7;
}

.row-failed {
  color: #b00020;
}

.row-deleting {
  opacity: 0.6;
  background-color: #fff5f5;
}

/* document status badges (mirroring FA chat badges with rounded-square edges) */
.doc-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
}

.doc-badge.upload {
  --badge-color: #28a745;
  --badge-bg: #e9f6ee;
  color: #155724;
}

.doc-badge.processing {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  --pulse-color: rgba(30, 126, 235, 0.35);
  color: #0b3d91;
}

.store-processing {
  margin-right: 8px;
  font-size: 10px;
}

.doc-badge.deleting {
  --badge-color: #dc3545;
  --badge-bg: #fde8ea;
  --pulse-color: rgba(220, 53, 69, 0.35);
  color: #721c24;
}

.doc-badge.failed {
  --badge-color: #b00020;
  --badge-bg: #fde8ea;
  color: #b00020;
}

.doc-badge.pulsing {
  animation: docBadgePulse 1.2s ease-in-out infinite;
}

@keyframes docBadgePulse {
  0% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
  50% {
    box-shadow: inset 0 0 6px 3px var(--pulse-color);
    opacity: 1;
  }
  100% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
}

.thinking-indicator {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  color: #333;
  padding: 10px 15px;
  border-radius: 18px;
  gap: 8px;
}


.company-docs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.company-docs-table th,
.company-docs-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.company-docs-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
}

.company-docs-table .doc-name {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-docs-table .doc-uploader {
  color: #666;
  font-size: 13px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-docs-table .doc-date {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.company-docs-table .doc-size {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.company-docs-table .doc-actions {
  text-align: right;
}

.company-docs-table .row-selected {
  background-color: #fff3cd;
}

.docs-summary {
  font-size: 13px;
  color: #555;
  padding: 6px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

/* Document stores table (list view) */
.doc-stores-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.doc-stores-table th,
.doc-stores-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}

.doc-stores-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
  text-align: left;
}

.doc-stores-table .store-company {
  background-color: #f0f7ff;
}

.doc-stores-table .store-owner {
  font-weight: 500;
}

.doc-stores-table .company-label {
  color: steelblue;
  font-weight: 600;
}

.doc-stores-table .user-label {
  color: #555;
}

.doc-stores-table .company-label {
  font-weight: 700;
}

.doc-stores-table .store-count {
  text-align: right;
  color: #666;
}

.doc-stores-table .store-size {
  text-align: right;
  color: #666;
}

.doc-stores-table .store-actions {
  text-align: right;
}

/* current month inline stats — admin-only nugget, matches the
   admin-only-row cream/orange tokens so it reads as "admin-visible detail". */
.usage-current-summary {
  margin-top: 8px;
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #fff8ef;
  box-shadow: inset 4px 0 0 0 #f0a957;
  padding: 6px 10px 6px 14px;
  border-radius: 0 4px 4px 0;
}

.usage-current-stat {
  font-weight: 600;
  color: #333;
}

.usage-current-sep {
  color: #bbb;
}

.usage-details-link {
  margin-left: 6px;
  font-weight: 600;
  color: steelblue;
  text-decoration: none;
  white-space: nowrap;
}

.usage-details-link:hover {
  text-decoration: underline;
  color: #3a6d9c;
}

/* fallback toggle (before history loads) — matches .usage-current-summary
   so both states read consistently with admin-only-row tokens. */
.usage-details-toggle {
  display: inline-block;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: steelblue;
  text-decoration: none;
  cursor: pointer;
  background-color: #fff8ef;
  box-shadow: inset 4px 0 0 0 #f0a957;
  padding: 6px 10px 6px 14px;
  border-radius: 0 4px 4px 0;
}

.usage-details-toggle:hover {
  text-decoration: underline;
  color: #3a6d9c;
}

/* Usage history table (invoice rollup) */
.usage-history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  font-weight: normal;
}

.usage-history-table th,
.usage-history-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

.usage-history-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
  text-align: left;
}

.usage-history-table .history-period {
  font-weight: 500;
  white-space: nowrap;
}

.usage-history-table .history-cost {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.usage-history-table .history-count {
  text-align: right;
  color: #555;
  font-variant-numeric: tabular-nums;
}

.usage-history-table .history-tokens {
  text-align: right;
  color: #888;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.usage-history-table .row-current-month {
  background-color: #f0f7ff;
}

.history-current-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #0b3d91;
  background: #e7f1ff;
  border: 1px solid #1e7eeb;
  border-radius: 4px;
  vertical-align: middle;
}

.usage-history-table tfoot td {
  border-top: 2px solid #ccc;
  border-bottom: none;
  background-color: #f8f9fa;
}

.history-total-row td {
  padding-top: 10px;
}

/* per-user breakdown */
.user-breakdown-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #dce1e6;
}
.user-breakdown-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
}
.user-breakdown-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}
.user-breakdown-period {
  font-size: 13px;
  color: #888;
}
.user-breakdown-period-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.user-breakdown-period-link {
  font-size: 12px;
  padding: 2px 8px;
  border: 1px solid #ccc;
  border-radius: 3px;
  color: #555;
  text-decoration: none;
}
.user-breakdown-period-link:hover {
  background: #f0f0f0;
  text-decoration: none;
}
.user-breakdown-period-link.active {
  background: #1a5ea8;
  color: #fff;
  border-color: #1a5ea8;
}
.user-breakdown-table .user-breakdown-user {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.usage-footnote {
  font-size: 11px;
  color: #999;
  font-style: italic;
  margin: 8px 0 0;
}
</style>
